"use client";

import { useIMQA } from "@/components/imqa-provider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { 
  AlertCircle, 
  Globe, 
  RefreshCw, 
  ArrowRight, 
  BadgeInfo,
  Terminal,
  UserCircle,
  Globe2,
  MousePointer,
  Network,
  Eye,
  Clock,
  Cpu,
  Bug,
  FileText,
  Zap,
  Database,
  Settings
} from "lucide-react";
import type { IMQA } from "@imqa/web-agent";

export function AdvancedTestButtons() {
  const { isInitialized } = useIMQA();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState<{ [key: string]: boolean }>({});
  const [IMQA, setIMQA] = useState<IMQA | null>(null);
  
  // Form states for testing
  const [testValues, setTestValues] = useState({
    userId: 'test-user-123',
    areaCode: 'US-CA',
    screenName: 'test-screen',
    actionName: 'test-action',
    logMessage: 'Test log message',
    errorMessage: 'Test error message',
    globalAttributes: '{"environment": "test", "version": "1.0.0"}',
    actionAttributes: '{"buttonId": "submit", "formType": "login"}',
    logAttributes: '{"level": "info", "component": "test"}',
    fetchUrl: 'https://jsonplaceholder.typicode.com/posts/1',
    xhrUrl: 'https://jsonplaceholder.typicode.com/users/1',
    websocketUrl: 'wss://echo.websocket.org',
  });

  // Import IMQA dynamically when needed
  const getIMQA = async () => {
    if (IMQA) return IMQA;
    
    try {
      const IMQAModule = await import('@imqa/web-agent');
      setIMQA(IMQAModule.default);
      return IMQAModule.default;
    } catch (error) {
      console.error('Failed to import IMQA:', error);
      toast({
        title: "Error",
        description: "Failed to load IMQA module",
        variant: "destructive",
      });
      return null;
    }
  };

  const withLoading = async (key: string, fn: () => Promise<void> | void) => {
    setIsLoading(prev => ({ ...prev, [key]: true }));
    try {
      await fn();
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setIsLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleSetUserId = async () => {
    const imqa = await getIMQA();
    if (!imqa) return;
    
    withLoading('setUserId', () => {
      imqa.setUserIdAttribute(testValues.userId);
      toast({
        title: "User ID Set",
        description: `User ID set to: ${testValues.userId}`,
      });
    });
  };

  const handleSetAreaCode = async () => {
    const imqa = await getIMQA();
    if (!imqa) return;
    
    withLoading('setAreaCode', () => {
      imqa.setAreaCodeAttribute(testValues.areaCode);
      toast({
        title: "Area Code Set",
        description: `Area code set to: ${testValues.areaCode}`,
      });
    });
  };

  const handleSetGlobalAttributes = async () => {
    const imqa = await getIMQA();
    if (!imqa) return;
    
    withLoading('setGlobalAttributes', () => {
      try {
        const attributes = JSON.parse(testValues.globalAttributes);
        imqa.setGlobalAttributes(attributes);
        toast({
          title: "Global Attributes Set",
          description: "Global attributes have been applied",
        });
      } catch {
        toast({
          title: "Invalid JSON",
          description: "Please enter valid JSON for global attributes",
          variant: "destructive",
        });
      }
    });
  };

  const handleSetScreenName = async () => {
    const imqa = await getIMQA();
    if (!imqa) return;
    
    withLoading('setScreenName', () => {
      // Note: setScreenAttribute is not available in the current IMQA type definition
      // Using a workaround to call it if it exists
      const imqaWithScreenAttribute = imqa as unknown as { setScreenAttribute?: (name: string) => void };
      if (imqaWithScreenAttribute.setScreenAttribute) {
        imqaWithScreenAttribute.setScreenAttribute(testValues.screenName);
        toast({
          title: "Screen Name Set",
          description: `Screen name set to: ${testValues.screenName}`,
        });
      } else {
        toast({
          title: "Method Not Available",
          description: "setScreenAttribute method is not available in the current IMQA version",
          variant: "destructive",
        });
      }
    });
  };

  const handleAddAction = async () => {
    const imqa = await getIMQA();
    if (!imqa) return;
    
    withLoading('addAction', () => {
      try {
        const attributes = testValues.actionAttributes ? JSON.parse(testValues.actionAttributes) : {};
        imqa.addAction(testValues.actionName, attributes);
        toast({
          title: "Action Recorded",
          description: `Action "${testValues.actionName}" has been recorded`,
        });
      } catch {
        toast({
          title: "Invalid JSON",
          description: "Please enter valid JSON for action attributes",
          variant: "destructive",
        });
      }
    });
  };

  const handleRecordLog = async () => {
    const imqa = await getIMQA();
    if (!imqa) return;
    
    withLoading('recordLog', () => {
      try {
        const attributes = testValues.logAttributes ? JSON.parse(testValues.logAttributes) : {};
        imqa.recordLog(testValues.logMessage, attributes);
        toast({
          title: "Log Recorded",
          description: `Log message recorded: ${testValues.logMessage}`,
        });
      } catch {
        toast({
          title: "Invalid JSON",
          description: "Please enter valid JSON for log attributes",
          variant: "destructive",
        });
      }
    });
  };

  const handleRecordException = async () => {
    const imqa = await getIMQA();
    if (!imqa) return;
    
    withLoading('recordException', () => {
      const error = new Error(testValues.errorMessage);
      imqa.recordException(error);
      toast({
        title: "Exception Recorded",
        description: `Exception recorded: ${testValues.errorMessage}`,
      });
    });
  };

  const handleThrowError = () => {
    withLoading('throwError', () => {
      throw new Error(testValues.errorMessage);
    });
  };

  const handleConsoleError = () => {
    withLoading('consoleError', () => {
      console.error(testValues.errorMessage, { timestamp: new Date().toISOString() });
      toast({
        title: "Console Error",
        description: "Console error has been logged",
      });
    });
  };

  const handleGetSessionInfo = async () => {
    const imqa = await getIMQA();
    if (!imqa) return;
    
    withLoading('getSessionInfo', () => {
      const sessionId = imqa.getSessionId();
      const sessionUrl = imqa.getSessionUrl();
      
      toast({
        title: "Session Information",
        description: (
          <div className="space-y-2">
            <div>Session ID: {sessionId || 'Not available'}</div>
            {sessionUrl && <div>Session URL: {sessionUrl}</div>}
          </div>
        ),
      });
    });
  };

  const handleNetworkCapture = async (enable: boolean) => {
    const imqa = await getIMQA();
    if (!imqa) return;
    
    withLoading('networkCapture', () => {
      if (enable) {
        imqa.enableAdvancedNetworkCapture();
        toast({
          title: "Network Capture Enabled",
          description: "Advanced network capture has been enabled",
        });
      } else {
        imqa.disableAdvancedNetworkCapture();
        toast({
          title: "Network Capture Disabled",
          description: "Advanced network capture has been disabled",
        });
      }
    });
  };

  const handleSessionRecorder = async (action: 'stop' | 'resume') => {
    const imqa = await getIMQA();
    if (!imqa) return;
    
    withLoading('sessionRecorder', () => {
      if (action === 'stop') {
        imqa.stopSessionRecorder();
        toast({
          title: "Session Recorder Stopped",
          description: "Session recording has been stopped",
        });
      } else {
        imqa.resumeSessionRecorder();
        toast({
          title: "Session Recorder Resumed",
          description: "Session recording has been resumed",
        });
      }
    });
  };

  const handleFetchTest = () => {
    withLoading('fetchTest', async () => {
      const response = await fetch(testValues.fetchUrl);
      const data = await response.json();
      
      toast({
        title: "Fetch Test Completed",
        description: `Response received from ${testValues.fetchUrl}`,
      });
      
      console.log('Fetch response:', data);
    });
  };

  const handleXhrTest = () => {
    withLoading('xhrTest', () => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', testValues.xhrUrl);
      xhr.onload = function () {
        if (xhr.status === 200) {
          toast({
            title: "XHR Test Completed",
            description: `Response received from ${testValues.xhrUrl}`,
          });
          console.log('XHR response:', xhr.responseText);
        }
      };
      xhr.onerror = function () {
        toast({
          title: "XHR Error",
          description: "Failed to complete XHR request",
          variant: "destructive",
        });
      };
      xhr.send();
    });
  };

  const handleWebSocketTest = () => {
    withLoading('websocketTest', () => {
      try {
        const ws = new WebSocket(testValues.websocketUrl);
        
        ws.onopen = () => {
          toast({
            title: "WebSocket Connected",
            description: `Connected to ${testValues.websocketUrl}`,
          });
          ws.send('Hello WebSocket!');
        };
        
        ws.onmessage = (event) => {
          console.log('WebSocket message:', event.data);
        };
        
        ws.onclose = () => {
          toast({
            title: "WebSocket Closed",
            description: "WebSocket connection has been closed",
          });
        };
        
        ws.onerror = () => {
          toast({
            title: "WebSocket Error",
            description: "Failed to connect to WebSocket",
            variant: "destructive",
          });
        };
        
        // Close after 3 seconds
        setTimeout(() => {
          ws.close();
        }, 3000);
      } catch {
        toast({
          title: "WebSocket Error",
          description: "Failed to create WebSocket connection",
          variant: "destructive",
        });
      }
    });
  };

  const handleLongTask = () => {
    withLoading('longTask', () => {
      // Simulate a long-running task
      const start = Date.now();
      while (Date.now() - start < 60) {
        // Busy wait for 60ms to trigger longtask detection
      }
      
      toast({
        title: "Long Task Completed",
        description: "A long-running task has been executed",
      });
    });
  };

  const handleUserInteraction = (type: string) => {
    toast({
      title: "User Interaction",
      description: `${type} event has been triggered`,
    });
  };

  const handleInputChange = (field: keyof typeof testValues, value: string) => {
    setTestValues(prev => ({ ...prev, [field]: value }));
  };

  if (!isInitialized) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">IMQA Web Agent Not Initialized</h3>
        <p className="text-muted-foreground">
          Please initialize the IMQA Web Agent first to test its features.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs defaultValue="core" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="core">Core APIs</TabsTrigger>
          <TabsTrigger value="network">Network</TabsTrigger>
          <TabsTrigger value="errors">Errors</TabsTrigger>
          <TabsTrigger value="session">Session</TabsTrigger>
          <TabsTrigger value="interactions">Interactions</TabsTrigger>
        </TabsList>

        <TabsContent value="core" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Core API Tests
              </CardTitle>
              <CardDescription>Test basic IMQA Web Agent functionality</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="userId">User ID</Label>
                  <div className="flex gap-2">
                    <Input
                      id="userId"
                      value={testValues.userId}
                      onChange={(e) => handleInputChange('userId', e.target.value)}
                      placeholder="Enter user ID"
                    />
                    <Button 
                      onClick={handleSetUserId}
                      disabled={isLoading.setUserId}
                      className="shrink-0"
                    >
                      <UserCircle className="h-4 w-4 mr-2" />
                      Set User ID
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="areaCode">Area Code</Label>
                  <div className="flex gap-2">
                    <Input
                      id="areaCode"
                      value={testValues.areaCode}
                      onChange={(e) => handleInputChange('areaCode', e.target.value)}
                      placeholder="Enter area code"
                    />
                    <Button 
                      onClick={handleSetAreaCode}
                      disabled={isLoading.setAreaCode}
                      className="shrink-0"
                    >
                      <Globe2 className="h-4 w-4 mr-2" />
                      Set Area Code
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="screenName">Screen Name</Label>
                  <div className="flex gap-2">
                    <Input
                      id="screenName"
                      value={testValues.screenName}
                      onChange={(e) => handleInputChange('screenName', e.target.value)}
                      placeholder="Enter screen name"
                    />
                    <Button 
                      onClick={handleSetScreenName}
                      disabled={isLoading.setScreenName}
                      className="shrink-0"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Set Screen Name
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="globalAttributes">Global Attributes (JSON)</Label>
                  <div className="flex gap-2">
                    <Textarea
                      id="globalAttributes"
                      value={testValues.globalAttributes}
                      onChange={(e) => handleInputChange('globalAttributes', e.target.value)}
                      placeholder='{"key": "value"}'
                      rows={2}
                      className="font-mono text-sm"
                    />
                    <Button 
                      onClick={handleSetGlobalAttributes}
                      disabled={isLoading.setGlobalAttributes}
                      className="shrink-0"
                    >
                      <Database className="h-4 w-4 mr-2" />
                      Set Attributes
                    </Button>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button 
                    onClick={handleGetSessionInfo}
                    disabled={isLoading.getSessionInfo}
                    variant="outline"
                  >
                    <BadgeInfo className="h-4 w-4 mr-2" />
                    Get Session Info
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="network" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Network className="h-5 w-5" />
                Network Tests
              </CardTitle>
              <CardDescription>Test network monitoring and requests</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fetchUrl">Fetch URL</Label>
                <div className="flex gap-2">
                  <Input
                    id="fetchUrl"
                    value={testValues.fetchUrl}
                    onChange={(e) => handleInputChange('fetchUrl', e.target.value)}
                    placeholder="Enter URL for fetch test"
                  />
                  <Button 
                    onClick={handleFetchTest}
                    disabled={isLoading.fetchTest}
                    className="shrink-0"
                  >
                    <Globe className="h-4 w-4 mr-2" />
                    Test Fetch
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="xhrUrl">XHR URL</Label>
                <div className="flex gap-2">
                  <Input
                    id="xhrUrl"
                    value={testValues.xhrUrl}
                    onChange={(e) => handleInputChange('xhrUrl', e.target.value)}
                    placeholder="Enter URL for XHR test"
                  />
                  <Button 
                    onClick={handleXhrTest}
                    disabled={isLoading.xhrTest}
                    className="shrink-0"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Test XHR
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="websocketUrl">WebSocket URL</Label>
                <div className="flex gap-2">
                  <Input
                    id="websocketUrl"
                    value={testValues.websocketUrl}
                    onChange={(e) => handleInputChange('websocketUrl', e.target.value)}
                    placeholder="Enter WebSocket URL"
                  />
                  <Button 
                    onClick={handleWebSocketTest}
                    disabled={isLoading.websocketTest}
                    className="shrink-0"
                  >
                    <Zap className="h-4 w-4 mr-2" />
                    Test WebSocket
                  </Button>
                </div>
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={() => handleNetworkCapture(true)}
                  disabled={isLoading.networkCapture}
                  variant="outline"
                >
                  Enable Network Capture
                </Button>
                <Button 
                  onClick={() => handleNetworkCapture(false)}
                  disabled={isLoading.networkCapture}
                  variant="outline"
                >
                  Disable Network Capture
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="errors" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bug className="h-5 w-5" />
                Error Handling Tests
              </CardTitle>
              <CardDescription>Test error reporting and exception handling</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="errorMessage">Error Message</Label>
                <Input
                  id="errorMessage"
                  value={testValues.errorMessage}
                  onChange={(e) => handleInputChange('errorMessage', e.target.value)}
                  placeholder="Enter error message"
                />
              </div>

              <div className="flex gap-2 flex-wrap">
                <Button 
                  onClick={handleRecordException}
                  disabled={isLoading.recordException}
                  variant="destructive"
                >
                  <AlertCircle className="h-4 w-4 mr-2" />
                  Record Exception
                </Button>
                <Button 
                  onClick={handleThrowError}
                  disabled={isLoading.throwError}
                  variant="destructive"
                >
                  <Bug className="h-4 w-4 mr-2" />
                  Throw Error
                </Button>
                <Button 
                  onClick={handleConsoleError}
                  disabled={isLoading.consoleError}
                  variant="outline"
                >
                  <Terminal className="h-4 w-4 mr-2" />
                  Console Error
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="session" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Session & Logging Tests
              </CardTitle>
              <CardDescription>Test session recording and custom logging</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="actionName">Action Name</Label>
                <div className="flex gap-2">
                  <Input
                    id="actionName"
                    value={testValues.actionName}
                    onChange={(e) => handleInputChange('actionName', e.target.value)}
                    placeholder="Enter action name"
                  />
                  <Button 
                    onClick={handleAddAction}
                    disabled={isLoading.addAction}
                    className="shrink-0"
                  >
                    Add Action
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="actionAttributes">Action Attributes (JSON)</Label>
                <Textarea
                  id="actionAttributes"
                  value={testValues.actionAttributes}
                  onChange={(e) => handleInputChange('actionAttributes', e.target.value)}
                  placeholder='{"key": "value"}'
                  rows={2}
                  className="font-mono text-sm"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="logMessage">Log Message</Label>
                <div className="flex gap-2">
                  <Input
                    id="logMessage"
                    value={testValues.logMessage}
                    onChange={(e) => handleInputChange('logMessage', e.target.value)}
                    placeholder="Enter log message"
                  />
                  <Button 
                    onClick={handleRecordLog}
                    disabled={isLoading.recordLog}
                    className="shrink-0"
                  >
                    Record Log
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="logAttributes">Log Attributes (JSON)</Label>
                <Textarea
                  id="logAttributes"
                  value={testValues.logAttributes}
                  onChange={(e) => handleInputChange('logAttributes', e.target.value)}
                  placeholder='{"key": "value"}'
                  rows={2}
                  className="font-mono text-sm"
                />
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={() => handleSessionRecorder('stop')}
                  disabled={isLoading.sessionRecorder}
                  variant="outline"
                >
                  Stop Session Recorder
                </Button>
                <Button 
                  onClick={() => handleSessionRecorder('resume')}
                  disabled={isLoading.sessionRecorder}
                  variant="outline"
                >
                  Resume Session Recorder
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="interactions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MousePointer className="h-5 w-5" />
                User Interaction Tests
              </CardTitle>
              <CardDescription>Test user interaction and performance monitoring</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Button 
                  onClick={() => handleUserInteraction('Click')}
                  variant="outline"
                >
                  <MousePointer className="h-4 w-4 mr-2" />
                  Click Event
                </Button>
                <Button 
                  onDoubleClick={() => handleUserInteraction('Double Click')}
                  variant="outline"
                >
                  <MousePointer className="h-4 w-4 mr-2" />
                  Double Click Event
                </Button>
                <Button 
                  onMouseDown={() => handleUserInteraction('Mouse Down')}
                  variant="outline"
                >
                  <MousePointer className="h-4 w-4 mr-2" />
                  Mouse Down Event
                </Button>
                <Button 
                  onMouseUp={() => handleUserInteraction('Mouse Up')}
                  variant="outline"
                >
                  <MousePointer className="h-4 w-4 mr-2" />
                  Mouse Up Event
                </Button>
              </div>

              <div className="space-y-2">
                <Label>Capture Options Test</Label>
                <p className="text-sm text-muted-foreground">
                  These buttons test captureText/captureId and data-imqa=&quot;ignore&quot; options.
                  Toggle options in Feature Toggles tab to see changes.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <Button
                    id="btn-with-id"
                    onClick={() => handleUserInteraction('Click with ID + Text')}
                    variant="outline"
                  >
                    Button with ID &amp; Text
                  </Button>
                  <Button
                    onClick={() => handleUserInteraction('Click with Text only')}
                    variant="outline"
                  >
                    Button Text Only
                  </Button>
                  <Button
                    id="btn-id-only"
                    onClick={() => handleUserInteraction('Click with ID only (no visible text)')}
                    variant="outline"
                  >
                    <MousePointer className="h-4 w-4" />
                  </Button>
                  <Button
                    data-imqa="ignore"
                    onClick={() => {
                      toast({
                        title: "Ignored Button Clicked",
                        description: "This click should NOT generate a span (data-imqa=\"ignore\")",
                      });
                    }}
                    variant="destructive"
                  >
                    Ignored Button (data-imqa=&quot;ignore&quot;)
                  </Button>
                </div>
                <div className="mt-2 p-3 bg-muted rounded-md text-sm space-y-1">
                  <p><strong>captureText=true:</strong> span includes innerText, Operation Name uses text first</p>
                  <p><strong>captureText=false:</strong> span excludes innerText, Operation Name skips to id or tag</p>
                  <p><strong>captureId=true:</strong> span includes element id</p>
                  <p><strong>captureId=false:</strong> span excludes element id</p>
                  <p><strong>data-imqa=&quot;ignore&quot;:</strong> no span/trace created at all</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Performance Tests</Label>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleLongTask}
                    disabled={isLoading.longTask}
                    variant="outline"
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    Trigger Long Task
                  </Button>
                  <Button 
                    onClick={() => {
                      // Trigger a large DOM manipulation
                      const elements = [];
                      for (let i = 0; i < 1000; i++) {
                        const div = document.createElement('div');
                        div.textContent = `Element ${i}`;
                        elements.push(div);
                      }
                      const container = document.createElement('div');
                      elements.forEach(el => container.appendChild(el));
                      document.body.appendChild(container);
                      setTimeout(() => document.body.removeChild(container), 100);
                      
                      toast({
                        title: "DOM Manipulation",
                        description: "Large DOM manipulation completed",
                      });
                    }}
                    variant="outline"
                  >
                    <Cpu className="h-4 w-4 mr-2" />
                    DOM Manipulation
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Form Interactions</Label>
                <div className="space-y-2">
                  <Input 
                    placeholder="Test input field (onChange will be captured)"
                    onChange={() => handleUserInteraction('Input Change')}
                  />
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    handleUserInteraction('Form Submit');
                  }}>
                    <Button type="submit" variant="outline" className="w-full">
                      <ArrowRight className="h-4 w-4 mr-2" />
                      Submit Form
                    </Button>
                  </form>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
